import * as React from "react";
import { AlertTriangle, ShieldCheck, ShieldAlert, AlertCircle, Loader2 } from "lucide-react";
import { cn } from "../../lib/utils";
import {
  Card,
  CardHeader,
  CardContent,
  CardTitle,
} from "./card";
import { CurrencyValue, PercentValue } from "./value-display";
import { Text, MonoText, Label } from "./typography";
import { Badge } from "./badge";

/**
 * Risk level configuration with colors and icons
 */
const RISK_CONFIG = {
  low: {
    label: "Low Risk",
    icon: ShieldCheck,
    bgColor: "bg-success/10",
    borderColor: "border-success/30",
    leftBorderColor: "border-l-green-500",
    textColor: "text-success",
    badgeVariant: "success",
    description: "Your portfolio is well diversified",
  },
  medium: {
    label: "Medium Risk",
    icon: AlertCircle,
    bgColor: "bg-warning/10",
    borderColor: "border-warning/30",
    leftBorderColor: "border-l-yellow-500",
    textColor: "text-warning",
    badgeVariant: "warning",
    description: "Some positions exceed 20% of portfolio",
  },
  high: {
    label: "High Risk",
    icon: AlertTriangle,
    bgColor: "bg-destructive/10",
    borderColor: "border-destructive/30",
    leftBorderColor: "border-l-orange-500",
    textColor: "text-destructive",
    badgeVariant: "destructive",
    description: "Significant concentration in few positions",
  },
  critical: {
    label: "Critical Risk",
    icon: ShieldAlert,
    bgColor: "bg-destructive/10",
    borderColor: "border-destructive/30",
    leftBorderColor: "border-l-red-500",
    textColor: "text-destructive",
    badgeVariant: "destructive",
    description: "Over 50% in a single position",
  },
};

/**
 * ConcentrationRisk Component
 * Displays portfolio concentration risk analysis
 *
 * @param {Object} props
 * @param {Object} props.data - Concentration risk data from API
 * @param {boolean} props.loading - Loading state
 */
export function ConcentrationRisk({ data, loading = false }) {
  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-2 pt-3 px-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-sm font-medium">Concentration Risk</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="px-4 pb-3">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card>
        <CardHeader className="pb-2 pt-3 px-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-sm font-medium">Concentration Risk</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="px-4 pb-3">
          <Text variant="secondary" size="sm" className="text-center py-4">
            No data available
          </Text>
        </CardContent>
      </Card>
    );
  }

  const riskLevel = data.riskLevel || "low";
  const config = RISK_CONFIG[riskLevel];
  const RiskIcon = config.icon;
  const threshold = data.threshold || 20;
  const concentratedPositions = data.concentratedPositions || [];

  return (
    <Card>
      <CardHeader className="pb-2 pt-3 px-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <RiskIcon className={cn("h-4 w-4", config.textColor)} />
            <CardTitle className="text-sm font-medium">Concentration Risk</CardTitle>
          </div>
          <Badge variant={config.badgeVariant} size="sm">
            {config.label}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-3 space-y-3">
        {/* Risk description */}
        <div className={cn(
          "rounded-md p-3 border border-l-4",
          config.bgColor,
          config.borderColor,
          config.leftBorderColor
        )}>
          <Text size="sm" className={config.textColor}>
            {config.description}
          </Text>
          {concentratedPositions.length === 0 && riskLevel === "low" && (
            <Text size="xs" variant="secondary" className="mt-1">
              No position exceeds {threshold}% of your portfolio
            </Text>
          )}
        </div>

        {/* Concentrated positions list */}
        {concentratedPositions.length > 0 && (
          <div className="space-y-2">
            <Label className="text-xs">Positions Above {threshold}%</Label>
            <div className="space-y-2">
              {concentratedPositions.map((position, index) => (
                <div
                  key={position.holdingId || index}
                  className="flex items-center justify-between p-2 rounded-md bg-muted/50"
                >
                  <div className="flex-1 min-w-0">
                    <Text size="sm" className="font-medium truncate">
                      {position.assetName}
                    </Text>
                    {position.ticker && (
                      <MonoText size="xs" className="text-muted-foreground">
                        {position.ticker}
                      </MonoText>
                    )}
                  </div>
                  <div className="flex flex-col items-end ml-2">
                    <PercentValue
                      value={position.concentrationPercent}
                      size="sm"
                      showSign={false}
                      colorize={false}
                      className={cn(
                        position.concentrationPercent >= 50
                          ? "text-destructive"
                          : position.concentrationPercent >= 30
                          ? "text-warning"
                          : "text-foreground"
                      )}
                    />
                    <CurrencyValue
                      value={position.totalValueEUR}
                      size="xs"
                      className="text-muted-foreground"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Summary stats */}
        {data.highestConcentration && (
          <div className="pt-2 border-t border-border">
            <div className="flex items-center justify-between text-xs">
              <Text variant="secondary" size="xs">Largest Position</Text>
              <div className="flex items-center gap-2">
                <Text size="xs" className="font-medium truncate max-w-[120px]">
                  {data.highestConcentration.assetName}
                </Text>
                <PercentValue
                  value={data.highestConcentration.concentrationPercent}
                  size="xs"
                  showSign={false}
                  colorize={false}
                />
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default ConcentrationRisk;
